import React, { Component } from 'react';
import { connect } from 'react-redux'
import ProgressModal from './progressModal';
import AuthActions from '../../reducers/authRedux'
import SigningActions from '../../reducers/signingRedux'
import { getDocumentDisplayName } from '../../utils/WorkflowUtil';

class DocumentSigner extends Component {
    constructor(props) {
        super(props);

        this.continueTimer = null
        this.continueDate = null
        this.returnCheckTimer = null
        // Check sessionStorage to see if we navigated to Adobe (survives page reload)
        this.hasNavigatedToAdobe = sessionStorage.getItem('hasNavigatedToAdobe') === 'true'

        this.state = {
            signingState: props.signingState,
            forms: props.forms,
            isSpouse: props.isSpouse,
            spouseFirstName: props.spouseFirstName,
            signingUri: props.signingUri,
            canContinue: false,
            primaryFirstName: props.primaryFirstName,
        }
    }

    componentDidMount() {
        // Listen for when user returns to page (e.g., via back button)
        window.addEventListener('popstate', this.handlePopState);
        window.addEventListener('pageshow', this.handlePageShow);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        
        // Check immediately if we came back from Adobe (don't wait for visibility event)
        // BUT: Don't cancel if we're on the callback page (successful completion)
        const isCallbackPage = window.location.pathname.includes('esignature');
        
        if (this.hasNavigatedToAdobe && 
            this.state.signingState === 'in_progress' &&
            window.location.hostname === 'localhost' &&
            !isCallbackPage) {
            // We're back on our page after going to Adobe - check if user completed
            this.returnCheckTimer = setTimeout(() => {
                this.checkIfUserReturnedWithoutCompleting();
            }, 300);
        }
        
        // If we're on callback page, clear the flag to allow normal processing
        if (isCallbackPage) {
            sessionStorage.removeItem('hasNavigatedToAdobe');
            this.hasNavigatedToAdobe = false;
        }
    }

    componentWillReceiveProps(newProps) {
        if (newProps.signingState !== this.state.signingState) {
            if (newProps.signingState === 'in_progress') {
                // reset the last activity time to give us the full 20 mins
                this.props.setLastActivityTime(new Date().getTime())
                this.setState({ canContinue: false })
                this.startTimer();
            }
            // Clear the flag when signing completes or is canceled
            if (newProps.signingState === 'complete' || newProps.signingState === 'canceled') {
                sessionStorage.removeItem('hasNavigatedToAdobe');
                this.hasNavigatedToAdobe = false;
            }
            this.setState({ signingState: newProps.signingState })
        }

        if (newProps.forms !== this.state.forms) {
            this.setState({ forms: newProps.forms })
        }

        if (newProps.isSpouse !== this.state.isSpouse) {
            this.setState({ isSpouse: newProps.isSpouse })
        }

        if (newProps.spouseFirstName !== this.state.spouseFirstName) {
            this.setState({ spouseFirstName: newProps.spouseFirstName })
        }

        if (newProps.primaryFirstName !== this.state.primaryFirstName) {
            this.setState({ primaryFirstName: newProps.primaryFirstName })
        }

        if (newProps.signingUri !== this.state.signingUri) {
            this.setState({ signingUri: newProps.signingUri })
            if (newProps.signingUri && this.state.canContinue) {
                // Mark that we're navigating to Adobe (persist across page reload)
                sessionStorage.setItem('hasNavigatedToAdobe', 'true');
                // Persist signing context so eSignatureCallback can restore it after the page reload
                sessionStorage.setItem('signingEnvelopeId', newProps.envelopeId || '');
                sessionStorage.setItem('signingRecipientId', newProps.recipientId || '');
                sessionStorage.setItem('signingCompleteRoute', newProps.signingCompleteRoute || '');
                this.hasNavigatedToAdobe = true;
                window.location.href = newProps.signingUri;
            }
        }
    }

    componentWillUnmount() {
        // Clean up event listeners and timers
        window.removeEventListener('popstate', this.handlePopState);
        window.removeEventListener('pageshow', this.handlePageShow);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        
        if (this.continueTimer) {
            clearTimeout(this.continueTimer);
        }
        if (this.returnCheckTimer) {
            clearTimeout(this.returnCheckTimer);
        }
    }

    handlePopState = (event) => {
        // popstate fires immediately when back/forward button is pressed
        // This is the most reliable way to detect back button navigation
        if (this.state.signingState === 'in_progress') {
            // Clear any existing timer
            if (this.returnCheckTimer) {
                clearTimeout(this.returnCheckTimer);
            }
            // Check very quickly
            this.returnCheckTimer = setTimeout(() => {
                this.checkIfUserReturnedWithoutCompleting();
            }, 100);
        }
    }

    handlePageShow = (event) => {
        // pageshow event fires when navigating back to page via browser back button
        // event.persisted is true when page is loaded from cache (bfcache)
        if (this.state.signingState === 'in_progress') {
            // Clear any existing timer
            if (this.returnCheckTimer) {
                clearTimeout(this.returnCheckTimer);
            }
            // Check immediately with short delay (just enough for callback to start if it's going to)
            this.returnCheckTimer = setTimeout(() => {
                this.checkIfUserReturnedWithoutCompleting();
            }, 500);
        }
    }

    handleVisibilityChange = () => {
        // Only check if user already navigated to Adobe and came back
        if (this.state.signingState === 'in_progress' && 
            document.visibilityState === 'visible' && 
            this.hasNavigatedToAdobe &&
            window.location.hostname === 'localhost') {
            // User came back to our page after navigating to Adobe
            // Clear any existing timer
            if (this.returnCheckTimer) {
                clearTimeout(this.returnCheckTimer);
            }
            
            // Check quickly - user came back from Adobe
            this.returnCheckTimer = setTimeout(() => {
                this.checkIfUserReturnedWithoutCompleting();
            }, 300);
        }
    }

    checkIfUserReturnedWithoutCompleting() {
        // If still in 'in_progress' state after returning to page,
        // user likely pressed back button without completing
        if (this.state.signingState === 'in_progress') {
            // User came back without completing - cancel signing
            this.props.cancelSigning();
            // Reset flag in both memory and storage
            this.hasNavigatedToAdobe = false;
            sessionStorage.removeItem('hasNavigatedToAdobe');
            sessionStorage.removeItem('signingEnvelopeId');
            sessionStorage.removeItem('signingRecipientId');
            sessionStorage.removeItem('signingCompleteRoute');
        }
        // Clear the timer reference
        this.returnCheckTimer = null;
    }

    startTimer() {
        this.continueTimer = setTimeout(() => {
            if (this.state.signingUri) {
                // Use full-page navigation (original behavior)
                // Mark that we're navigating to Adobe (persist across page reload)
                sessionStorage.setItem('hasNavigatedToAdobe', 'true');
                // Persist signing context so eSignatureCallback can restore it after the page reload
                sessionStorage.setItem('signingEnvelopeId', this.props.envelopeId || '');
                sessionStorage.setItem('signingRecipientId', this.props.recipientId || '');
                sessionStorage.setItem('signingCompleteRoute', this.props.signingCompleteRoute || '');
                this.hasNavigatedToAdobe = true;
                window.location.href = this.state.signingUri;
            } else {
                this.setState({ canContinue: true })
            }

        }, 5 * 1000);
    }
    
    render() {
        var visible = this.state.signingState === 'in_progress' || this.state.signingState === 'processing'
        var forms = []
        var titleKey = ''
        var subTitleKey = ''

        if (this.state.forms) {
            forms = this.state.forms.map(x => x.toUpperCase()).sort()
        }

        var key = forms.join('')

        if (key) {
            titleKey = 'ESignatureLabels.' + (this.state.isSpouse ? 'Spouse.' : 'Self.') + 'Title' + key
            subTitleKey = 'ESignatureLabels.' + (this.state.isSpouse ? 'Spouse.' : 'Self.') + 'SubTitle' + key
        } else {
            titleKey = 'ESignatureLabels.Additional.Title'
            subTitleKey = 'ESignatureLabels.Additional.' + (this.state.isSpouse ? 'Spouse.' : 'Self.') + 'ContentForm'
        }

        var params = {}
        if (this.props.isAdditional) {
            titleKey = 'ESignatureLabels.Additional.Title'

            var formName = (forms && forms.length > 0) 
                ? getDocumentDisplayName(forms[0], this.props.lang)
                : "Form";

            params = {
                FormName: formName
            }

            titleKey = 'ESignatureLabels.' + (this.state.isSpouse ? 'Spouse.' : 'Self.') + 'TitleAdditional';
            subTitleKey = 'ESignatureLabels.' + (this.state.isSpouse ? 'Spouse.' : 'Self.') + 'SubTitleAdditional';           
        }

        return (
            <div style={{ width: '0px', height: '0px' }}>
                <ProgressModal visible={visible} titleKey={titleKey} subTitleKey={subTitleKey}
                    layoutType={this.props.layoutType} spouseName={this.props.spouseFirstName}
                    primaryName={this.props.primaryFirstName}
                    params={params}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        lang: state.layout.lang,
        layoutType: state.layout.layoutType,
        signingState: state.signing.signingState,
        forms: state.signing.forms, 
        isSpouse: state.signing.isSpouse,
        spouseFirstName: state.signing.spouseFirstName,
        signingUri: state.signing.signingUri,
        isAdditional: state.signing.isAdditional,
        primaryFirstName: state.signing.primaryFirstName,
        envelopeId: state.signing.envelopeId,
        recipientId: state.signing.recipientId,
        signingCompleteRoute: state.signing.signingCompleteRoute
    }
}

const mapDispatchToProps = (dispatch) => ({
    setLastActivityTime: (lastActivityTime) => dispatch(AuthActions.setLastActivityTime(lastActivityTime)),
    cancelSigning: () => dispatch(SigningActions.cancelSigning())
})

export default connect(mapStateToProps, mapDispatchToProps)(DocumentSigner)
